import React, { useState, useRef, useEffect, useTransition } from "react";
import "./index.scss";
import { DocInfo } from "../../types/files";
import {
  Box,
  Button,
  Checkbox,
  getColor,
  Icons,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import RevisionTag from "../RevisionTag";
import classNames from "classnames";
import utilsCommon from "../../shared/utils/common";
import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import utilsDocs from "../../shared/utils/docs";
import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
import { handleDocPreview } from "../../shared/utils/api";
import { useTranslation } from "react-i18next";
import Thumbnail from "../Thumbnail";
interface DocCardProps {
  row: DocInfo;
  asideId: string;
  openDocAsideWithId?: (v: string) => void;
  setDocSettingInfo: (req: {
    x: number;
    y: number;
    syncId: string;
    docInfo: DocInfo;
  }) => void;
  needCheckbox?: boolean;
  page?: string;
  checkboxGroup?: string[];
  checkCheckbox?: (syncId: string) => void;
}

const DocCard: React.FC<DocCardProps> = ({
  row,
  asideId,
  openDocAsideWithId,
  setDocSettingInfo,
  needCheckbox = false,
  checkCheckbox,
  checkboxGroup,
  page,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { getContentFavorite } = useDocumentStore();
  const { updateStarItemFavorite } = useWorkgroupStore();

  return (
    <Box
      classes={classNames("docCard", {
        asided: row.syncId === asideId,
        selected: checkboxGroup?.includes(row.syncId),
      })}
      id={`docCard-${row.syncId}`}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDocSettingInfo({
          x: e.pageX - 240,
          y: e.pageY - 61,
          syncId: row.syncId,
          docInfo: row,
        });
      }}
      onClick={(e: any) => {
        e.preventDefault();
        e.stopPropagation();
        openDocAsideWithId && openDocAsideWithId(row.syncId);
      }}
    >
      <Box classes="docCard-upper">
        {needCheckbox && (
          <Checkbox
            classes={classNames("docCard-upper--checkbox", {
              isChecked: checkboxGroup?.includes(row.syncId),
            })}
            onClick={() => checkCheckbox && checkCheckbox(row.syncId)}
            check={checkboxGroup?.includes(row.syncId)}
          />
        )}
        <Button
          btnStyle={"icon"}
          size="sm"
          classes="docCard-setting"
          onClick={(e) => {
            e.stopPropagation();
            setDocSettingInfo({
              x: e.pageX - 240,
              y: e.pageY - 61,
              syncId: row.syncId,
              docInfo: row,
            });
          }}
          leadingIcon={<Icons variant="line" label="moreVertical" />}
        />
        <Box classes="docCard-thumbnail">
          <Thumbnail
            syncId={row.syncId}
            revisionNo={row.revisionNo}
            contentName={row.contentName}
          />
        </Box>
      </Box>
      <Box classes="docCard-under">
        <Box direction={"column"} width="calc(100% - 34px)">
          <Box alignItems={"center"} classes="docCard-title">
            <Tooltip message={row.contentName}>
              <Typography
                type="body2_rg"
                ellipsis
                classes={classNames({
                  "hover-text-underline": utilsCommon.checkMenuAvailble(
                    "preview",
                    row
                  ),
                })}
                onClick={() => {
                  utilsCommon.checkMenuAvailble("preview", row) &&
                    handleDocPreview({
                      permission: "view",
                      workgroupId: row?.workgroup.workgroupId,
                      syncId: row.syncId,
                      toast,
                      t,
                      revisionNo: row?.revisionNo,
                    });
                }}
              >
                {row.contentName}
              </Typography>
            </Tooltip>
            <RevisionTag
              revisionNo={row.revisionNo}
              size={"sm"}
              classes={"mg ml-2"}
            />
          </Box>
          {page === "favorite" && (
            <Box
              alignItems={"center"}
              classes={"docCard-under--date"}
              height={20}
            >
              <Typography
                type="body4_rg"
                ellipsis
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {row?.workgroup.workgroupId === "NONEWORKGROUP"
                  ? "My Box"
                  : row?.workgroup.workgroupName}
              </Typography>
            </Box>
          )}
          <Box
            alignItems={"center"}
            classes={"docCard-under--date"}
            height={20}
          >
            <Typography
              type="body4_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {utilsCommon.renderDateFormat(row.revisionDate)}
            </Typography>
            {!!row.readRequestDate && row.readRequestDate > 0 && (
              <Tooltip message={t("header.setAlarm.request")} height={20}>
                <Icons
                  variant="fill"
                  label="alertCircle"
                  stroke={getColor("COLOR_ICON_ERROR")}
                  classes={"mg ml-4"}
                />
              </Tooltip>
            )}
            {row.checkoutUser.userId !== "" && (
              <Tooltip
                height={20}
                message={
                  <Box direction="column">
                    <Typography
                      exactColor="white"
                      type="body4_rg"
                      fontWeight={700}
                    >
                      {row?.checkoutUser.userName ?? row?.checkoutUser.userId}
                    </Typography>
                    <Typography exactColor="white" type="body4_rg">
                      {t("file.revisionInDesktop")}
                    </Typography>
                  </Box>
                }
              >
                <Icons
                  variant="fill"
                  label="clientRevision"
                  classes={"mg ml-4"}
                />
              </Tooltip>
            )}
            {row.concurrentUserId !== null && row.concurrentUserId !== "" && (
              <Tooltip
                height={20}
                message={
                  row?.checkoutUser.userName + " " + t("file.revisionInWeb")
                }
              >
                <Icons variant="fill" label="webRevision" classes={"mg ml-4"} />
              </Tooltip>
            )}
            {!!row?.signCount && row?.signCount > 0 && (
              <Tooltip
                message={t("file.commentWithCount", {
                  count: row?.signCount,
                })}
                classes="mg ml-4"
              >
                <Icons variant="line" label="memo" />
              </Tooltip>
            )}
          </Box>
        </Box>

        <Button
          btnStyle="icon"
          classes={classNames("mg ml-auto docCard-bookmark", {
            isOn: row.favoritDate > 0,
          })}
          onClick={(e: any) => {
            e.stopPropagation();
            if (!row) return;
            getContentFavorite({
              syncIds: [row?.syncId],
              flag: row?.favoritDate <= 0,
              page: page,
            });
            updateStarItemFavorite({
              syncIds: [row?.syncId],
              flag: row?.favoritDate <= 0,
            });
          }}
          leadingIcon={
            <Icons
              variant="fill"
              label="star"
              isColored={row.favoritDate > 0}
              size={20}
            />
          }
        />
      </Box>
    </Box>
  );
};
export default DocCard;
